import { useState } from 'react'
import { ErroApi } from '../lib/api'
import { useSessao } from '../lib/sessao'
import { Mail, Lock, Eye, EyeOff } from 'lucide-react'
import { Marca } from '../componentes/Marca'
import './entrar.css'

/**
 * Login.
 *
 * ─── A MENSAGEM DE ERRO É UMA SÓ ──────────────────────────────────
 *
 * E-mail inexistente e senha errada dizem a MESMA coisa. Distinguir os
 * dois transforma a tela num verificador de quem é cliente da clínica —
 * e a API foi construída para não denunciar isso (roda o argon2 mesmo
 * sem usuário, para o tempo não entregar). Seria uma pena a tela
 * devolver de graça o que o back-end gasta 10 ms para esconder.
 *
 * ─── POR QUE DUAS COLUNAS ─────────────────────────────────────────
 *
 * Era um cartão sozinho no meio de uma tela vazia. Login de software de
 * saúde é onde a clínica decide se confia — e uma caixa cinza no vácuo
 * não diz nada sobre quem está do outro lado.
 *
 * A metade da direita não é enfeite: ela responde "por que este
 * sistema?" para quem chegou pelo link de um convite e nunca viu o
 * site. Ilustração de LINHA, não foto de banco de imagem: envelhece
 * melhor, pesa menos, e não obriga a escolher etnia e gênero de um
 * modelo — que num produto de saúde brasileiro é problema de verdade.
 */
export function Entrar({ aoCriarConta }: { aoCriarConta?: () => void } = {}) {
  const { entrar } = useSessao()
  const [email, setEmail] = useState('')
  const [senha, setSenha] = useState('')
  const [mostrando, setMostrando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  async function submeter(e: React.FormEvent) {
    e.preventDefault()
    setErro(null); setEnviando(true)
    try {
      await entrar(email.trim(), senha)
    } catch (err) {
      setErro(
        err instanceof ErroApi && err.status === 429
          ? 'Muitas tentativas. Espere um minuto e tente de novo.'
          : 'E-mail ou senha não conferem.',
      )
      setEnviando(false)
    }
  }

  return (
    <main className="ent">
      <div className="ent__folha">
        {/* ── a metade que trabalha ───────────────────────────────── */}
        <section className="ent__lado">
          <header className="ent__marca"><Marca tamanho={26} /></header>

          <form className="ent__form" onSubmit={submeter}>
            <div>
              <h1>Entrar</h1>
              <p className="ajuda">Acesse a agenda da sua clínica.</p>
            </div>

            {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

            <div className="campo">
              <label htmlFor="email">E-mail</label>
              <div className="ent__campo-icone">
                <span aria-hidden="true"><Mail size={16} strokeWidth={1.9} /></span>
                <input id="email" className="entrada" type="email" required
                       autoComplete="username" autoFocus inputMode="email"
                       placeholder="voce@suaclinica.com.br"
                       value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>

            <div className="campo">
              {/* "Esqueci minha senha" na linha do RÓTULO, à direita.
                  Embaixo do botão — onde estava — é o pior lugar: quem
                  tem pressa clica em Entrar, erra, e só então procura. E
                  o e-mail já digitado vai junto, para não escrever duas
                  vezes. */}
              <div className="ent__rotulo-linha">
                <label htmlFor="senha">Senha</label>
                <a className="ent__link"
                   href={`/recuperar${email.trim() ? `?email=${encodeURIComponent(email.trim())}` : ''}`}>
                  Esqueci minha senha
                </a>
              </div>
              <div className="ent__campo-icone">
                <span aria-hidden="true"><Lock size={16} strokeWidth={1.9} /></span>
                <input id="senha" className="entrada"
                       type={mostrando ? 'text' : 'password'} required
                       autoComplete="current-password"
                       value={senha} onChange={(e) => setSenha(e.target.value)} />
                <button type="button" className="ent__olho"
                        onClick={() => setMostrando(!mostrando)}
                        aria-pressed={mostrando}
                        aria-label={mostrando ? 'Ocultar a senha' : 'Mostrar a senha'}>
                  {mostrando ? <EyeOff size={16} strokeWidth={1.9} /> : <Eye size={16} strokeWidth={1.9} />}
                </button>
              </div>
            </div>

            <button className="botao ent__entrar" type="submit" disabled={enviando}>
              {enviando ? 'Entrando…' : 'Entrar'}
            </button>

            <p className="ent__rodape">
              Ainda não tem conta?{' '}
              <button type="button" className="ent__botao-link"
                      onClick={() => aoCriarConta?.()}>Teste 14 dias</button>
            </p>
          </form>

          <footer className="ent__pe ajuda">
            Isolamento por clínica, backup diário com restauração ensaiada
            e trilha de acesso ao prontuário.
          </footer>
        </section>

        {/* ── a metade que responde "por que este sistema?" ────────── */}
        <aside className="ent__vitrine" aria-hidden="true">
          <blockquote className="ent__fala">
            <p>
              Marcaram dois pacientes na mesma sala de novo. O sistema deixou.
            </p>
            <cite>a frase que não deve mais acontecer</cite>
          </blockquote>

          <ul className="ent__pontos">
            <li><strong>Uma sala, um horário.</strong> A regra é do banco, não da tela.</li>
            <li><strong>Prontuário assinado não muda.</strong> Correção é adendo, ao lado.</li>
            <li><strong>Quem abriu, fica registrado.</strong> Sempre.</li>
          </ul>

          <IlustracaoAgenda />
        </aside>
      </div>
    </main>
  )
}

/**
 * A grade da agenda, em traço.
 *
 * Desenhada à mão em SVG e não trazida de biblioteca: são 20 linhas, o
 * assunto é exatamente o do produto, e uma ilustração externa custaria
 * uma dependência mais do que vale.
 *
 * `currentColor` em tudo: assim ela acompanha o tema em vez de precisar
 * de duas versões.
 */
function IlustracaoAgenda() {
  const horas = [0, 1, 2, 3, 4, 5]
  const blocos = [
    { x: 0, y: 0, h: 1, cheio: true },
    { x: 1, y: 1, h: 2, cheio: false },
    { x: 2, y: 0, h: 1, cheio: false },
    { x: 0, y: 2, h: 2, cheio: false },
    { x: 2, y: 3, h: 2, cheio: true },
    { x: 1, y: 4, h: 1, cheio: false },
  ]
  return (
    <svg className="ent__svg" viewBox="0 0 300 220" fill="none" role="img"
         aria-label="Ilustração de uma grade de agenda">
      {horas.map((h) => (
        <line key={h} x1="26" y1={18 + h * 34} x2="292" y2={18 + h * 34}
              stroke="currentColor" strokeWidth="1" opacity=".18" />
      ))}
      {horas.slice(0, 5).map((h) => (
        <text key={h} x="6" y={22 + h * 34} fontSize="9" fill="currentColor" opacity=".4">
          {`${8 + h}h`}
        </text>
      ))}
      {[0, 1, 2].map((c) => (
        <line key={c} x1={26 + c * 89} y1="10" x2={26 + c * 89} y2="196"
              stroke="currentColor" strokeWidth="1" opacity=".12" />
      ))}
      {blocos.map((b, i) => (
        <rect key={i}
              x={32 + b.x * 89} y={20 + b.y * 34}
              width="76" height={b.h * 34 - 8} rx="6"
              fill={b.cheio ? 'currentColor' : 'none'}
              fillOpacity={b.cheio ? '.14' : '0'}
              stroke="currentColor" strokeWidth="1.4"
              opacity={b.cheio ? '.9' : '.45'} />
      ))}
    </svg>
  )
}
